import { FilterTableInfo } from "./types";
import { CustomColumn } from "../..";

export type StatusOptions = {
  status?: Record<string, string>;
  product?: Record<string, string>;
  termsheet_status?: Record<string, string>;
  internalStatus?: Record<string, string>;
  invoice_status?: Record<string, string>;
  internal_status?: Record<string, string>;
  onboarding_status?: Record<string, string>;
  departments?: Record<string, string>;
  is_active?: Record<string, string>;
  approval?: Record<string, string>;
  transaction_type?: Record<string, string>;
  currency?: Record<string, string>;
};

export const getFilterTableInfo = (filterColumnTableName?: Record<string, any> | any[]): FilterTableInfo => {
  // Handle both object and array inputs
  let tableFlags: Record<string, any> = {};
  
  if (Array.isArray(filterColumnTableName)) {
    // If it's an array, check all elements for table type flags
    filterColumnTableName.forEach((item) => {
      if (item && typeof item === 'object') {
        Object.keys(item).forEach((key) => {
          if (key.includes('_details') || key === 'transactions') {
            tableFlags[key] = item[key];
          }
        });
      }
    });
  } else if (filterColumnTableName && typeof filterColumnTableName === 'object') {
    // If it's an object, use it directly
    tableFlags = filterColumnTableName;
  }

  const result = {
    isAccountDetails: "account_details" in tableFlags,
    isTermSheetDetails: ("termsheet_details" in tableFlags || "termsheets_details" in tableFlags),
    isCreditLimitDetails: "creditlimit_details" in tableFlags,
    isLeadDetails: "leads_details" in tableFlags,
    isMapDetails: "maps_details" in tableFlags,
    isUserDetails: "users_details" in tableFlags,
    isCountryDetails: "countries_details" in tableFlags,
    isBillingDetails: "billing_details" in tableFlags,
    isEmailDetails: "email_details" in tableFlags,
    isOnBoardingPipelineDetails: "onboarding_details" in tableFlags,
    isUserManagementDetails: "user_type_details" in tableFlags,
    isTransactionTable: "transactions" in tableFlags,
    isTradePartnerDetails: "trade_partner_details" in tableFlags,
    isInvoiceDetails: "invoice_details" in tableFlags,
  };
  
  return result;
};

export const getStatusOptions = (tableInfo: FilterTableInfo, statusMaster?: any): StatusOptions => {
  const {
    isAccountDetails,
    isTermSheetDetails,
    isCreditLimitDetails,
    isBillingDetails,
    isMapDetails,
    isEmailDetails,
    isOnBoardingPipelineDetails,
    isUserManagementDetails,
    isTransactionTable,
    isInvoiceDetails
  } = tableInfo;

  if (isAccountDetails) {
    return {
      status: {
        Registered: "REGISTERED",
        Onboarded: "ONBOARDED",
        Internal: "INTERNAL",
        Created: "CREATED",
        Onboarding: "ONBOARDING",
        Presales: "PRESALES",
      },
    };
  }
  if (isTermSheetDetails) {
    const statusOptions: any = {
      product: {
        Factoring: "factoring",
        "Reverse Factoring": "reverse_factoring",
      },
    };
    
    // Dynamically fetch termsheet status options from common-api if available
    if (statusMaster?.TERMSHEET) {
      const termsheetStatuses: Record<string, string> = {};
      statusMaster.TERMSHEET.forEach((item: any) => {
        termsheetStatuses[item.id] = item.id; // Show ID in dropdown, send ID to API
      });
      statusOptions.termsheet_status = termsheetStatuses;
    } else {
      // Fallback to hardcoded values if API is not available
      statusOptions.termsheet_status = {
        Rejected: "REJECTED_INTERNAL",
        "Pending Approval": "PENDING_INTERNAL",
        "In Progress": "IN_PROGRESS",
        Approved: "APPROVED",
        "Customer Rejected": "REJECTED",
        "Invoice expired": "INVOICE_CANCELLED",
        "Termsheet expired": "TERMSHEET_CANCELLED",
      };
    }
    
    return statusOptions;
  }
  if (isCreditLimitDetails) {
    return {
      internalStatus: {
        "Credit Limit Rejected": "CL_REJECTED",
        "Rejected by sales": "REJECTED_BY_SALES",
        "Rejected by insurer": "REJECTED_BY_INSURER",
        "Pending Insurer": "CL_REQUESTED",
        "Pending Credit Limit Approval": "CL_REQUESTED",
        "Pending Sales": "PENDING_SALES",
        "Credit Limit Approved": "CL_APPROVED",
      },
    };
  }
  if (isBillingDetails) {
    return {
      invoice_status: {
        Approved: "PAID",
        Pending: "PAYMENT_PENDING",
        Rejected: "EXPIRED",
      },
    };
  }
  if (isInvoiceDetails) {
    // For marketplace invoices, we'll use the MARKETPLACE_INVOICE_STATUS from common-keys
    // This will be dynamically populated by the customStatusOptions passed from DataTableInvoices
    return {
      invoice_status: {
        // Default fallback values - will be overridden by customStatusOptions
        "Incomlend Review": "INCOMLEND_REVIEW",
        "On Marketplace": "ON_MARKETPLACE",
        "Invoice Progress": "INVOICE_PROGRESS",
        "Invoice Paid": "INVOICE_PAID",
        Collections: "COLLECTIONS",
        Delayed: "DELAYED",
      },
    };
  }
  if (isMapDetails) {
    return {
      status: {
        Approved: "true",
        Pending: "false",
      },
      product: {
        Factoring: "factoring",
        "Reverse Factoring": "reverse_factoring",
      },
    };
  }
  if (isEmailDetails) {
    return {
      status: {
        Sent: "SENT",
        Delivered: "DELIVERED",
        Failed: "FAILED",
      },
    };
  }
  if (isOnBoardingPipelineDetails) {
    return {
      internal_status: {
        "Credit Limit Rejected": "CL_REJECTED",
        "Rejected by sales": "REJECTED_BY_SALES",
        "Rejected by insurer": "REJECTED_BY_INSURER",
        "Pending Insurer": "CL_REQUESTED",
        "Pending Credit Limit Approval": "CL_REQUESTED",
        "Pending Sales": "PENDING_SALES",
        "Credit Limit Approved": "CL_APPROVED",
      },
    };
  }
  if (isUserManagementDetails) {
    return {
      is_active: {
        True: "true",
        False: "false"
      },
    };
  }
  if (isTransactionTable) {
    return {
      status: {
        "Confirmed by Finance": "Confirmed by Finance",
        "Confirmed by operations": "Confirmed by operations"
      },
      transaction_type: {
        "01.Invoice Purchase": "01.Invoice Purchase",
        "02.Invoice Advance Amount": "02.Invoice Advance Amount", 
        "03.Invoice Repayment": "03.Invoice Repayment",
        "04.Invoice Capital Repayment": "04.Invoice Capital Repayment",
        "05.Invoice Discount Amount": "05.Invoice Discount Amount",
        "06.Transaction Fee": "06.Transaction Fee",
        "07.Credit Insurance": "07.Credit Insurance", 
        "08.Invoice Balance Payment": "08.Invoice Balance Payment",
        "031.Partial Payment Topup": "031.Partial Payment Topup",
        "054.Invoice Discount Amount Overdue": "054.Invoice Discount Amount Overdue",
        "70.Deposit": "70.Deposit",
        "80.Withdrawal": "80.Withdrawal",
        "88.Annual membership fee": "88.Annual membership fee",
        "89.Maintenance fee": "89.Maintenance fee"
      },
      currency: {
        USD: "USD",
        EUR: "EUR", 
        GBP: "GBP",
        SGD: "SGD",
        INR: "INR",
        JPY: "JPY",
        CNY: "CNY",
        HKD: "HKD",
        AUD: "AUD",
        CAD: "CAD"
      }
    };
  }
  return {};
};

export const getStatusDisplayValue = (statusOptions: StatusOptions, statusKey: keyof StatusOptions, apiValue: string): string => {
  const statusMapping = statusOptions[statusKey];
  if (statusMapping) {
    const displayValue = Object.keys(statusMapping).find(
      (key) => statusMapping[key] === apiValue
    );
    return displayValue || apiValue;
  }
  return apiValue;
};

export const getStatusApiValue = (statusOptions: StatusOptions, statusKey: string, displayValue: string): string => {
  const statusMapping = statusOptions[statusKey as keyof typeof statusOptions];
  if (statusMapping && statusMapping[displayValue]) {
    return statusMapping[displayValue];
  }
  return displayValue;
};

export const getFilterColumnsForTable = (tableInfo: FilterTableInfo) => {
  const {
    isAccountDetails,
    isTermSheetDetails,
    isCreditLimitDetails,
    isLeadDetails,
    isMapDetails,
    isUserDetails,
    isCountryDetails,
    isBillingDetails,
    isEmailDetails,
    isOnBoardingPipelineDetails,
    isUserManagementDetails,
    isTransactionTable,
    isInvoiceDetails
  } = tableInfo;

  // Custom logic for Investor Allocations table
  // If the tableInfo is empty (no special type), and the columns include 'importer' and 'funder_name', return only those
  // This works because InvestorAllocations passes ['importer', 'funder_name'] as filterColumnTableName
  if (
    tableInfo &&
    Object.values(tableInfo).every((v) => v === false) // no special type
  ) {
    return ['importer', 'funder_name'];
  }

  const accountDetailsFilterColumns = ["country", "status", "created_at"];
  const termsheetsFilterColumns = ["termsheet_status", "product"];
  const creditLimitFilterColumns = [
    "buyerCountry",
    "internalStatus",
    "approved_amount",
    "dateRequestedMktg",
    "dateRequested",
  ];
  const leadFilterColumns = ["country", "profile"];
  const mapFilterColumns = ["product", "status"];
  const usersFilterColumns = ["opted_marketing_updates"];
  const countryFilterColumns = ["name"];
  const billingFilterColumns = ["invoice_status"];
  const invoiceDetailsFilterColumns = ["invoice_status", "invoice_due_date"];
  const emailFilterColumns = ["status"];
  const onBoardingPipelineFilterColumns = [
    "internal_status",
    "buyer_country",
    "seller_country",
  ];
  const userManagementFilterColumns = ["is_active"];
  const transactionTableFilterColumns = ["currency", "status", "transaction_type"];

  let result: string[] = [];

  if (isAccountDetails) result = accountDetailsFilterColumns;
  else if (isTermSheetDetails) result = termsheetsFilterColumns;
  else if (isCreditLimitDetails) result = creditLimitFilterColumns;
  else if (isLeadDetails) result = leadFilterColumns;
  else if (isMapDetails) result = mapFilterColumns;
  else if (isUserDetails) result = usersFilterColumns;
  else if (isCountryDetails) result = countryFilterColumns;
  else if (isBillingDetails) result = billingFilterColumns;
  else if (isInvoiceDetails) result = invoiceDetailsFilterColumns;
  else if (isEmailDetails) result = emailFilterColumns;
  else if (isOnBoardingPipelineDetails) result = onBoardingPipelineFilterColumns;
  else if (isUserManagementDetails) result = userManagementFilterColumns;
  else if (isTransactionTable) result = transactionTableFilterColumns;
  else result = [];

  return result;
};

export const getFilteredCustomColumns = <T extends Record<string, any>>(
  customColumns: CustomColumn,
  tableInfo: FilterTableInfo,
): CustomColumn => {
  const filterColumns = getFilterColumnsForTable(tableInfo);
  
  if (filterColumns.length > 0) {
    return Object.fromEntries(
      Object.entries(customColumns || {}).filter(([key]) =>
        filterColumns.includes(key)
      )
    );
  }
  
  return customColumns;
};
